import get from "lodash/get";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Stack } from "@mui/material";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";

import { MeasurementIcon } from "../../icons/MeasurementIcon";
import type { GroupedMeasurement, WorkflowReducer } from "../workflowReducer";

import { MeasurementComponentDropdown } from "./MeasurementComponentDropdown";
import { MeasurementTypeDropdown } from "./MeasurementTypeDropdown";

import { ConfirmationDialog } from "../../shared/ConfirmationDialog";

import AssetDropdown from "../../data/Assets/AssetDropdown/AssetDropdown";
import { useAssetsBySubstation } from "../../data/Assets/useAssetsBySubstation";
import AssetTypesDropdown from "../../shared/AssetTypesDropdown/AssetTypesDropdown";
import {
  getAssetTypeKey,
  getAssetTypeLabel,
  ManagedSurveyAssetType,
  managedSurveyAssetType,
} from "../../shared/constants/managedSurveyAssetTypes";
import {
  AccessLocation,
  MeasurementAccessory,
  MeasurementComponent,
  MeasurementType,
  SubLocation,
} from "../../shared/enums/Result";
import { CableNaming } from "../../shared/enums/UTP2";
import { DeleteButton } from "../../shared/FormLayout/IconButtons/DeleteButton";
import { getTranslatedSubLocationName } from "../../shared/helpers/SubLocationHelpers";
import { Asset } from "../../shared/interfaces/Asset";
import { AccessLocationDropdown } from "./AccessLocationDropdown";

interface Props {
  measurement: GroupedMeasurement;
  editMode?: boolean;
  onChange: (updatedMeasure: GroupedMeasurement, togglePanelAccordion: boolean) => void;
  onGroupDeleteButtonClick: (groupId: string) => void;
  cableNaming?: CableNaming;
  isDuplicate?: boolean;
  assetId?: number;
  workflowType?: string;
  workflowReducer?: WorkflowReducer;
}

const useStyles = makeStyles((theme) => ({
  gridContainer: {
    alignItems: "center",
    padding: theme.spacing(1),
    justifyContent: "space-between",
    paddingBottom: theme.spacing(2),
  },
  gridItem: {
    display: "flex",
    alignItems: "center",
  },
  icon: {
    height: "2em",
    width: "2em",
    marginRight: theme.spacing(3),
  },
  marginTopBottom: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  strongText: {
    fontSize: "1.2em",
    fontWeight: 500,
  },
  dividerGridItem: {
    flexGrow: 1,
    margin: theme.spacing(0, 1),
  },
  dividerContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  divider: {
    flex: "1 1 auto",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "nowrap",
  },
}));

export function MeasurementGroupHeader(props: Props) {
  const { t } = useTranslation();
  const classes = useStyles();

  const isGenericWorkflow = props.workflowType === "generic";
  const isWorkflowV1 = props.workflowReducer?.state.workflow.workflowDetail.version === 1;

  const {
    register,
    formState: { errors },
    setValue,
    trigger,
  } = useFormContext();

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const selectedAssetType = props.measurement.parentAssetType
    ? getAssetTypeKey(props.measurement.parentAssetType as ManagedSurveyAssetType)
    : undefined;
  const selectedAssetId = props.measurement.parentAssetId;

  const substationAssets = useAssetsBySubstation(
    props.workflowReducer?.state.workflow.workflowDetail.substation_id,
  ).data;
  const asset = substationAssets?.find((asset: Asset) => asset.id === selectedAssetId) ?? null;

  function onAssetChange(assets: Asset[]) {
    if (assets.length !== 1) return;
    const asset = assets[0];

    if (!substationAssets?.includes(asset)) return;

    setValue(`measurementsTab.${props.measurement.groupId}.asset`, asset, { shouldValidate: true });

    const updatedMeasure: GroupedMeasurement = {
      ...props.measurement,
      parentAssetId: asset?.id ?? undefined,
      parentAssetType: asset?.assetTypeKey ? getAssetTypeLabel(asset?.assetTypeKey) : undefined,
      parentAssetName: asset?.name ?? undefined,
      panels: [],
    };

    if (
      props.measurement.parentAssetType !== undefined &&
      getAssetTypeLabel(asset.assetTypeKey) !== props.measurement.parentAssetType
    ) {
      Object.assign(updatedMeasure, {
        access_location: undefined,
        component: undefined,
        subloc: undefined,
        type: undefined,
        accessory: undefined,
      });
    }

    props.onChange(updatedMeasure, true);
  }

  function onAssetTypeChange(assetType: string | undefined) {
    const assetTypeLabel = getAssetTypeLabel(assetType);
    setValue(`measurementsTab.${props.measurement.groupId}.assetType`, assetTypeLabel, { shouldValidate: true });
    const updatedMeasure: GroupedMeasurement = {
      ...props.measurement,
      access_location: undefined,
      component: undefined,
      subloc: undefined,
      type: undefined,
      accessory: undefined,
      panels: [],
      parentAssetType: assetTypeLabel,
    };
    props.onChange(updatedMeasure, false);
  }

  function onMeasurementTypeChange(measurementType?: MeasurementType, measurementAccessory?: MeasurementAccessory) {
    setValue(`measurementsTab.${props.measurement.groupId}.measurementType`, measurementType, { shouldValidate: true });

    const updatedMeasure: GroupedMeasurement = {
      ...props.measurement,
      type: measurementType,
      accessory: measurementAccessory,
    };

    props.onChange(updatedMeasure, false);
  }

  function onMeasurementComponentChange(measurementComponent?: MeasurementComponent, subLocation?: SubLocation) {
    setValue(`measurementsTab.${props.measurement.groupId}.measurementComponent`, measurementComponent, {
      shouldValidate: true,
    });

    const updatedMeasure: GroupedMeasurement = {
      ...props.measurement,
      component: measurementComponent,
      subloc: subLocation,
    };

    props.onChange(updatedMeasure, false);
  }

  function onAccessLocationChange(accessLocation?: AccessLocation) {
    setValue(`measurementsTab.${props.measurement.groupId}.accessLocation`, accessLocation, { shouldValidate: true });

    const updatedMeasure: GroupedMeasurement = {
      ...props.measurement,
      access_location: accessLocation,
    };

    props.onChange(updatedMeasure, false);
  }

  function handleGroupDeleteButtonClick() {
    props.measurement.panels.length > 0
      ? setShowConfirmationDialog(true)
      : props.onGroupDeleteButtonClick(props.measurement.groupId);
  }

  function deleteSelectedGroup() {
    props.onGroupDeleteButtonClick(props.measurement.groupId);
  }

  function closeConfirmationDialog() {
    setShowConfirmationDialog(false);
  }

  // This stops the component revalidating itself when being initially mounted.
  // Without this, when adding a new blank measurement, it will validate as a blank row showing errors under each box.
  // Revalidation trigger here just removes errors as soon as the user removes the duplicated measurement,
  // otherwise its confusing if the error remains.
  const isMounted = useRef(false);
  useEffect(() => {
    if (isMounted.current && !props.isDuplicate) trigger("measurementsTab");
    else if (!isMounted.current) isMounted.current = true;
  }, [props.isDuplicate, trigger]);

  return (
    <Grid container columns={isWorkflowV1 ? 13 : 20} className={classes.gridContainer} wrap="nowrap">
      {isGenericWorkflow && !isWorkflowV1 && (
        <>
          <Grid item className={classes.gridItem} xs={isWorkflowV1 ? 3 : 4}>
            {props.editMode ? (
              <AssetTypesDropdown
                minWidth="200px"
                isPrimeReact
                onAssetTypeChange={onAssetTypeChange}
                selectedAssetType={selectedAssetType}
                error={
                  get(errors, `measurementsTab.${props.measurement.groupId}.assetType`) &&
                  t("asset.empty", { val: t("asset.assetType") })
                }
                {...register(`measurementsTab.${props.measurement.groupId}.assetType`, {
                  required: true,
                  value: props.measurement.parentAssetType,
                })}
              />
            ) : (
              <Stack direction="row" spacing={2} alignItems="center">
                {props.measurement.type && (
                  <MeasurementIcon height="1.5em" width="1.5em" measurementType={props.measurement.type} />
                )}

                <Typography variant="h6" component="h2">
                  {t("results.measurementType", {
                    measurementType: `$t(MeasurementTypes.${props.measurement.parentAssetType} )`,
                  })}
                </Typography>
              </Stack>
            )}
          </Grid>
        </>
      )}
      {!isGenericWorkflow && !isWorkflowV1 && (
        <>
          <Grid item className={classes.gridItem} xs={isWorkflowV1 ? 3 : 4}>
            {props.editMode ? (
              <AssetDropdown
                width="100%"
                onAssetChange={onAssetChange}
                selectedAssets={asset ? [asset] : []}
                invalidMessage={t("asset.empty", { val: t("asset.asset") })}
                substationId={props.workflowReducer?.state.workflow.workflowDetail.substation_id}
                options={substationAssets ?? []}
                invalid={get(errors, `measurementsTab.${props.measurement.groupId}.asset`)}
                {...register(`measurementsTab.${props.measurement.groupId}.asset`, {
                  required: true,
                  value: props.measurement.parentAssetId,
                })}
              />
            ) : (
              <Stack direction="row" spacing={2} alignItems="center">
                {props.measurement.type && (
                  <MeasurementIcon height="1.5em" width="1.5em" measurementType={props.measurement.type} />
                )}

                <Typography variant="h6" component="h2">
                  {t("results.measurementType", {
                    measurementType: `$t(MeasurementTypes.${props.measurement.parentAssetName} )`,
                  })}
                </Typography>
              </Stack>
            )}
          </Grid>
        </>
      )}
      <Grid xs={1} item>
        <div className={classes.dividerContainer}>
          <Divider variant="fullWidth" className={classes.divider} />
        </div>
      </Grid>
      <Grid item className={classes.gridItem} xs={isWorkflowV1 ? 3 : 4}>
        {props.editMode ? (
          <MeasurementTypeDropdown
            disabled={!selectedAssetType && !selectedAssetId && !isWorkflowV1}
            selectedMeasurementType={props.measurement.type}
            selectedAccessory={props.measurement.accessory}
            className={"w-100"}
            invalid={get(errors, `measurementsTab.${props.measurement.groupId}.measurementType`)}
            invalidMessage={
              (get(errors, `measurementsTab.${props.measurement.groupId}.measurementType`)?.type === "duplicate" &&
                t("workflows.duplicateMeasurements")) ||
              t("workflows.measurementTypeRequired")
            }
            {...register(`measurementsTab.${props.measurement.groupId}.measurementType`, {
              required: true,
              value: props.measurement.type,
              validate: {
                duplicate: () => !props.isDuplicate,
              },
            })}
            onChange={onMeasurementTypeChange}
          />
        ) : (
          <Stack direction="row" spacing={2} alignItems="center">
            {props.measurement.type && (
              <MeasurementIcon height="1.5em" width="1.5em" measurementType={props.measurement.type} />
            )}

            <Typography variant="h6" component="h2">
              {t("results.measurementType", { measurementType: `$t(MeasurementTypes.${props.measurement.type} )` })}
            </Typography>

            {props.measurement.accessory && (
              <Typography
                variant="subtitle2"
                color="textSecondary"
                component="span"
                className={classes.marginTopBottom}
              >
                {t(`UltrasonicAccessory.${props.measurement.accessory}`)}
              </Typography>
            )}
          </Stack>
        )}
      </Grid>
      <Grid item className={classes.dividerGridItem} xs={1}>
        <div className={classes.dividerContainer}>
          <Divider variant="fullWidth" className={classes.divider} />
        </div>
      </Grid>

      <Grid item xs={isWorkflowV1 ? 3 : 4}>
        {props.editMode ? (
          <AccessLocationDropdown
            disabled={!selectedAssetType && !selectedAssetId && !isWorkflowV1}
            selectedAccessLocation={props.measurement.access_location}
            invalid={get(errors, `measurementsTab.${props.measurement.groupId}.accessLocation`)}
            invalidMessage={t("workflows.accessLocationRequired")}
            {...register(`measurementsTab.${props.measurement.groupId}.accessLocation`, {
              required: true,
              value: props.measurement.access_location,
            })}
            onChange={onAccessLocationChange}
          />
        ) : (
          <span>
            <div className={classes.marginTopBottom}>
              <Typography variant="body1" component="span" className={classes.strongText}>
                {props.measurement.access_location
                  ? t(`AccessLocations.${props.measurement.access_location}`)
                  : t("workflows.noAccessLocation")}
              </Typography>
            </div>
          </span>
        )}
      </Grid>
      <Grid item className={classes.dividerGridItem} xs={1}>
        <div className={classes.dividerContainer}>
          <Divider variant="fullWidth" className={classes.divider} />
        </div>
      </Grid>

      <Grid item xs={isWorkflowV1 ? 3 : 4}>
        {props.editMode ? (
          <MeasurementComponentDropdown
            disabled={!selectedAssetType && !selectedAssetId && !isWorkflowV1}
            selectedMeasurementComponent={props.measurement.component}
            selectedSubLocation={props.measurement.subloc}
            selectedMeasurementType={props.measurement.type}
            invalid={get(errors, `measurementsTab.${props.measurement.groupId}.measurementComponent`)}
            invalidMessage={t("workflows.measurementComponentRequired")}
            {...register(`measurementsTab.${props.measurement.groupId}.measurementComponent`, {
              required: true,
              value: props.measurement.component,
            })}
            onChange={onMeasurementComponentChange}
            cableNaming={props.cableNaming}
            assetType={
              managedSurveyAssetType[
                (isGenericWorkflow ? selectedAssetType : asset?.assetTypeKey) as keyof typeof managedSurveyAssetType
              ]
            }
          />
        ) : (
          <span>
            <div className={classes.marginTopBottom}>
              <Typography variant="body1" component="span" className={classes.strongText}>
                {t(`MeasurementComponents.${props.measurement.component}`)}
              </Typography>
            </div>

            {props.measurement.subloc && (
              <div className={classes.marginTopBottom}>
                <Typography variant="subtitle2" color="textSecondary" component="span" noWrap>
                  {props.measurement.component &&
                    getTranslatedSubLocationName(
                      props.measurement.subloc,
                      props.measurement.component,
                      props.cableNaming,
                    )}
                </Typography>
              </div>
            )}
          </span>
        )}
      </Grid>
      {isWorkflowV1 && (
        <Grid xs={1} item>
          <div className={classes.dividerContainer}>
            <Divider variant="fullWidth" className={classes.divider} />
          </div>
        </Grid>
      )}

      {props.editMode && (
        <>
          <Grid item xs={1} className={classes.actions}>
            <DeleteButton onClick={handleGroupDeleteButtonClick} />
          </Grid>
        </>
      )}

      {showConfirmationDialog && (
        <ConfirmationDialog
          message={t("workflows.removeMeasurementsDetailed", {
            measurementType: props.measurement.type ? t(`MeasurementTypes.${props.measurement.type}`) : "",
            assetType: t(`assetTypes.${getAssetTypeLabel(props.measurement.parentAssetType)}`),
          })}
          onAccept={deleteSelectedGroup}
          onClose={closeConfirmationDialog}
          visible={showConfirmationDialog}
          header={t("workflows.removeMeasurements")}
          closeMessage={t("common.cancel")}
          icon="warning"
        />
      )}
    </Grid>
  );
}
